import { useEffect, useMemo, useState } from 'react';

import {
  DatasetSessionCompareDefaultsProps,
  METRICS_COLUMNS,
  TextDisplayMode,
} from '@/Pages/DatasetSessionCompare/constants';
import { useComparisonViewDisplaySettings } from '@/Pages/DatasetSessionCompare/utils/useComparisonViewDisplaySettings';
import { useExamples, useRunsQuery } from '@/hooks/useSwr';

import { PlaygroundExperimentView } from '../components/PlaygroundExperimentView';
import {
  formatRunOutput,
  getDatasetSplits,
  getExampleRunsFromPlaygroundState,
  getPlaygroundStateWithReplacedFetchedRuns,
  needsToFetchFeedbackKeys,
} from '../utils/Playground.utils';
import { PromptOutputSectionHeader } from './PromptOutputSectionHeader';
import { useAlignEvaluatorInfo } from './hooks/useAlignEvaluator';
import {
  useDeep,
  useInput,
  usePlaygroundStore,
} from './hooks/usePlaygroundStore';
import { hasAnyNestedExamples } from './utils';

const DEFAULT_DISPLAY_SETTINGS: DatasetSessionCompareDefaultsProps = {
  textDisplayMode: TextDisplayMode.FULL,
  hideMetrics: true,
  hiddenMetricsColumns: Object.values(METRICS_COLUMNS).map((c) => c.name),
  hideFeedback: false,
  isReferenceInputHidden: false,
  isReferenceOutputHidden: false,
  language: 'json' as const,
};

export function PromptOutputSection() {
  const prompts = usePlaygroundStore(useDeep((state) => state.prompts));
  const datasetId = usePlaygroundStore((state) => state.datasetId);
  const isCreatingNewDataset = usePlaygroundStore(
    (state) => state.isCreatingNewDataset
  );
  const input = useInput();
  const inputSourceType = usePlaygroundStore((state) => state.inputSourceType);
  const evaluatorFeedbackKeys = usePlaygroundStore(
    (state) => state.evaluatorFeedbackKeys
  );
  const runsToFetch = usePlaygroundStore(
    useDeep((state) =>
      (
        state.prompts.flatMap((p) => p.runs?.map((r) => r.id) ?? []) ?? []
      ).filter(Boolean)
    )
  );
  const { isAlignEvaluatorV2 } = useAlignEvaluatorInfo();

  const displaySettingsState = useComparisonViewDisplaySettings({
    aggregateFeedbackKeys: evaluatorFeedbackKeys,
    localStorageStatePrefix: 'playground_display_settings',
    defaultSettings: DEFAULT_DISPLAY_SETTINGS,
  });

  const {
    setHideMetrics,
    setHiddenMetricsColumns,
    setHideFeedback,
    setIsReferenceOutputHidden,
    setIsReferenceInputHidden,
  } = displaySettingsState;

  useEffect(() => {
    setHideMetrics(true);
    setHiddenMetricsColumns(Object.values(METRICS_COLUMNS).map((c) => c.name));
    if (isCreatingNewDataset) {
      setIsReferenceOutputHidden(false);
      setHideFeedback(true);
      setIsReferenceInputHidden(false);
    } else {
      setHideFeedback(false);
    }
  }, [
    isCreatingNewDataset,
    setHideMetrics,
    setHiddenMetricsColumns,
    setHideFeedback,
    setIsReferenceOutputHidden,
    setIsReferenceInputHidden,
  ]);

  const datasetSplits = usePlaygroundStore((state) => state.datasetSplits);
  const examples = usePlaygroundStore((state) => state.examples);
  const setExamples = usePlaygroundStore((state) => state.setExamples);
  const { data: fetchedExamples } = useExamples(
    datasetId
      ? {
          dataset: datasetId ?? undefined,
          limit: 20,
          offset: 0,
          splits: getDatasetSplits(datasetSplits),
          select: [
            'id',
            'created_at',
            'modified_at',
            'name',
            'dataset_id',
            'source_run_id',
            'metadata',
            'inputs',
            'outputs',
            'attachment_urls',
          ],
          order: 'id',
          descending: false,
        }
      : null,
    undefined,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (fetchedExamples) {
      setExamples(fetchedExamples.map((e) => ({ ...e, edited: false })));
    }
  }, [fetchedExamples, setExamples]);

  const [numRunsFetched, setNumRunsFetched] = useState(0);
  const [needsFetchFeedbackKeys, setNeedsFetchFeedbackKeys] = useState(false);

  const runs = useRunsQuery(
    runsToFetch.length > 0
      ? {
          id: runsToFetch,
        }
      : null,
    undefined,
    {
      refreshInterval:
        numRunsFetched === runsToFetch.length && !needsFetchFeedbackKeys
          ? 0
          : 1000,
      onSuccess: (data) => {
        if (data.runs) {
          setNumRunsFetched(data.runs.length);
          const replacedRunsState = getPlaygroundStateWithReplacedFetchedRuns(
            prompts,
            data.runs
          );
          const allRuns = replacedRunsState?.flatMap((p) => p.runs ?? []);
          setNeedsFetchFeedbackKeys(
            needsToFetchFeedbackKeys(allRuns, evaluatorFeedbackKeys)
          );
        }
      },
    }
  );

  const exampleRuns = useMemo(
    () =>
      getExampleRunsFromPlaygroundState({
        isCreatingNewDataset,
        prompts,
        examples,
        datasetId,
        fetchedRuns: runs.data?.runs.map(formatRunOutput),
        input,
        extraReservedNames: isAlignEvaluatorV2 ? ['few_shot_examples'] : [],
      }) ?? [],
    [
      isCreatingNewDataset,
      prompts,
      examples,
      datasetId,
      runs.data?.runs,
      input,
      isAlignEvaluatorV2,
    ]
  );

  const isEditable = useMemo(() => {
    return (
      !hasAnyNestedExamples(exampleRuns) &&
      !!(datasetId || isCreatingNewDataset)
    );
  }, [exampleRuns, datasetId, isCreatingNewDataset]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PromptOutputSectionHeader
        displaySettingsState={displaySettingsState}
        exampleRuns={exampleRuns}
        isEditable={isEditable}
      />
      <div className="flex-grow overflow-auto">
        {inputSourceType === 'dataset' &&
        !(datasetId || isCreatingNewDataset) ? (
          <div className="m-8 text-center text-secondary">
            No dataset selected
          </div>
        ) : (
          <PlaygroundExperimentView
            datasetId={datasetId}
            displaySettingsState={displaySettingsState}
            exampleRuns={exampleRuns.slice(0, 20)}
            isEditable={isEditable}
          />
        )}
      </div>
    </div>
  );
}
